import { useEffect, useState } from "react";
import { Lock, Trash2, RefreshCw, Upload, ImagePlus, LogOut } from "lucide-react";
import { toast } from "sonner";
import {
  adminLogin, listContacts, deleteContact, addGallery, deleteGallery, getGallery,
} from "../lib/api";
import { GALLERY_CATEGORIES } from "../lib/site";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("zelaya_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  if (!token) {
    const onSubmit = async (e) => {
      e.preventDefault();
      setLoggingIn(true);
      try {
        await adminLogin(password);
        localStorage.setItem("zelaya_admin_token", password);
        setToken(password);
        toast.success("Welcome back.");
      } catch {
        toast.error("Incorrect password.");
      } finally {
        setLoggingIn(false);
      }
    };
    return (
      <section className="min-h-[70vh] flex items-center justify-center" data-testid="admin-login-section">
        <form onSubmit={onSubmit} className="bg-[#0c0c0c] border border-white/5 p-10 w-full max-w-md">
          <Lock className="text-[var(--gold)]" size={28} />
          <h1 className="mt-4 font-display text-3xl text-white">Admin Access</h1>
          <p className="mt-2 text-white/50 text-sm">Enter the admin password to manage submissions and gallery.</p>
          <label className="block mt-8">
            <span className="overline">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-3 px-0 outline-none text-white"
              data-testid="admin-password-input"
            />
          </label>
          <button disabled={loggingIn} className="btn-primary mt-8 w-full" data-testid="admin-login-btn">
            {loggingIn ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </section>
    );
  }

  return <AdminPanel onLogout={() => { localStorage.removeItem("zelaya_admin_token"); setToken(""); }} />;
}

function AdminPanel({ onLogout }) {
  const [tab, setTab] = useState("submissions");
  return (
    <section className="container-luxury py-16" data-testid="admin-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="overline">Admin</p>
          <h1 className="mt-3 font-display text-4xl text-white font-light">Operations Dashboard</h1>
        </div>
        <button onClick={onLogout} className="btn-ghost !py-3" data-testid="admin-logout-btn">
          <LogOut size={16} /> Sign Out
        </button>
      </div>

      <div className="flex gap-3 border-b border-white/10 mb-8">
        {[
          { key: "submissions", label: "Contact Submissions" },
          { key: "gallery", label: "Gallery Manager" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px ${
              tab === t.key ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "submissions" ? <Submissions /> : <GalleryManager />}
    </section>
  );
}

function Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listContacts();
      setItems(data);
    } catch {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await deleteContact(id);
      setItems((s) => s.filter((i) => i.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex justify-between items-center mb-6">
        <p className="text-white/60">{items.length} total submissions</p>
        <button onClick={load} className="btn-ghost !py-3" data-testid="refresh-submissions">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-white/50">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-white/50">No submissions yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-px bg-white/5">
          {items.map((s) => (
            <div key={s.id} className="bg-[#0A0A0A] p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`submission-${s.id}`}>
              <div className="md:col-span-3">
                <p className="text-white font-medium">{s.name}</p>
                <p className="text-white/50 text-sm">{s.phone}</p>
                <p className="text-white/50 text-sm break-all">{s.email}</p>
              </div>
              <div className="md:col-span-2">
                <p className="overline">Service</p>
                <p className="text-white/70 text-sm mt-1">{s.service || "—"}</p>
              </div>
              <div className="md:col-span-5">
                <p className="overline">Message</p>
                <p className="text-white/70 text-sm mt-1 whitespace-pre-wrap">{s.message}</p>
              </div>
              <div className="md:col-span-2 flex md:flex-col items-end gap-2">
                <span className={`text-xs uppercase tracking-[0.2em] ${s.email_sent ? "text-[var(--gold)]" : "text-white/40"}`}>
                  {s.email_sent ? "Emailed" : "Saved"}
                </span>
                <p className="text-white/40 text-xs">{new Date(s.created_at).toLocaleString()}</p>
                <button onClick={() => remove(s.id)} className="text-red-400/80 hover:text-red-400 text-xs flex items-center gap-1" data-testid={`delete-submission-${s.id}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "interior", description: "", image_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const data = await getGallery();
      setItems(data);
    } catch {
      toast.error("Failed to load gallery.");
    }
  };

  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large (max 2MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) {
      toast.error("Title and image are required.");
      return;
    }
    setBusy(true);
    try {
      await addGallery(form);
      toast.success("Added to gallery.");
      setForm({ title: "", category: "interior", description: "", image_url: "" });
      load();
    } catch {
      toast.error("Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await deleteGallery(id);
      setItems((s) => s.filter((i) => i.id !== id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="admin-gallery">
      <form onSubmit={submit} className="lg:col-span-5 bg-[#0c0c0c] border border-white/5 p-8">
        <ImagePlus className="text-[var(--gold)]" size={24} />
        <h3 className="mt-3 font-display text-2xl text-white">Add to Gallery</h3>

        <label className="block mt-6">
          <span className="overline">Title</span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-2 outline-none text-white" data-testid="gallery-title-input" />
        </label>
        <label className="block mt-6">
          <span className="overline">Category</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-2 outline-none text-white" data-testid="gallery-category-input">
            {GALLERY_CATEGORIES.filter((c) => c.key !== "all").map((c) => (
              <option key={c.key} value={c.key} className="bg-black">{c.label}</option>
            ))}
          </select>
        </label>
        <label className="block mt-6">
          <span className="overline">Description</span>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] py-2 outline-none text-white" data-testid="gallery-desc-input" />
        </label>
        <label className="block mt-6">
          <span className="overline">Image (max 2MB)</span>
          <input type="file" accept="image/*" onChange={onFile} className="mt-2 w-full text-white/70 file:bg-[var(--gold)] file:text-black file:border-0 file:px-4 file:py-2 file:mr-4 file:uppercase file:tracking-[0.2em] file:text-xs file:cursor-pointer" data-testid="gallery-file-input" />
        </label>
        {form.image_url && (
          <div className="mt-4 aspect-[4/3] border border-white/10 overflow-hidden">
            <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
        <button disabled={busy} className="btn-primary mt-8 w-full" data-testid="gallery-submit-btn">
          <Upload size={16} /> {busy ? "Saving…" : "Add Project"}
        </button>
      </form>

      <div className="lg:col-span-7">
        <p className="overline mb-4">Uploaded Projects ({items.length})</p>
        {items.length === 0 ? (
          <p className="text-white/50">No uploads yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="bg-[#0A0A0A] border border-white/5 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <p className="overline">{it.category}</p>
                  <p className="text-white mt-1">{it.title}</p>
                  <button onClick={() => remove(it.id)} className="mt-3 text-red-400/80 hover:text-red-400 text-xs flex items-center gap-1" data-testid={`delete-gallery-${it.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
